import {createSelector, createStructuredSelector} from 'reselect';
import {create} from 'zustand';

import {EditorPropertiesContextInterface} from 'notebook/notes/core-ui/NoteContext';
import {CellColors} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TABLE_EDITABLE_DEFAULT_STATE, TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {
  type TableMutationMethods,
  UNAVAILABLE_TABLE_MUTATIONS,
} from 'notebook/tables/store/logic/mutations/createTableMutations';

/**
 * This holds volatile state, or state that frequently changes due to evaluation or renders.
 * Everything inside here will be deep-compare memoized.
 */
export interface TableEditableVolatileProps {
  table: Table;
  staleRows?: Array<number>;
  errorRows?: Array<boolean>;
  warningRows?: Array<boolean>;
  computedTable: Array<Array<EvaluatedCell>>;
}

/**
 * The props that are diverted into {@link TableEditableSpreadsheetState} rather than kept on the
 * store's props. They change whenever the user interacts with a formula anywhere in the document,
 * so keeping them out of `stableProps` keeps them out of the composite `TableModel`.
 */
export type TableEditableStableProps = Omit<
  TableEditableProps,
  keyof TableEditableVolatileProps | keyof TableEditableSpreadsheetState
>;

/**
 * This holds data derived from redux that is populated into the store before the model is
 * generated. Keeping it here (rather than on TableEditable's props) keeps these values as internal
 * store state that does not pollute the internal or external component props. Add new redux-derived
 * data here as more of it is threaded into the store.
 */
export interface TableEditableReduxState {
  /**
   * The persisted collapsed state for this table from the redux `collapsedTables` UI slice
   * (keyed by collapse id), or `undefined` if the user has not toggled it. Read at the top-level
   * `TableEditable` wrapper and threaded through the store so selectors can derive the effective
   * collapsed state.
   */
  collapsedStateInRedux: boolean | undefined;
}

export const DEFAULT_TABLE_EDITABLE_REDUX_STATE: TableEditableReduxState = {
  collapsedStateInRedux: undefined,
};

/**
 * This holds data derived from `SpreadsheetStore` that is table-scoped and changes as the user
 * interacts with formulas. It is kept out of the store's props so that it does not invalidate the
 * composite `TableModel`: only the models that read it (today the axis cell models) are affected
 * when it changes, so a formula interaction does not re-render the whole table.
 */
export interface TableEditableSpreadsheetState {
  showExcessLabels: boolean;
  cellColors: CellColors | undefined;
}

export const DEFAULT_TABLE_EDITABLE_SPREADSHEET_STATE: TableEditableSpreadsheetState = {
  showExcessLabels: false,
  cellColors: undefined,
};

/**
 * This is the state in the Zustand store.  New non-volatile state can be added here.
 */
export interface TableEditableStore {
  stableProps: TableEditableStableProps;
  volatileProps: TableEditableVolatileProps;
  editorContext: EditorPropertiesContextInterface;
  reduxState: TableEditableReduxState;
  spreadsheetState: TableEditableSpreadsheetState;
  /**
   * The table's mutation methods, built once per table by `TableModelProvider` and relayed onto the
   * `TableModel` and its sub-models so that every consumer shares one instance. Every method reads
   * fresh state from the store when called, so this only changes when the provider's own inputs
   * (table ops, note context, `disableMergingCells`) do.
   */
  mutations: TableMutationMethods;
  state: TableEditableState;
  setProps: (stableProps: TableEditableStableProps, volatileProps: TableEditableVolatileProps) => void;
  setEditorContext: (editorContext: EditorPropertiesContextInterface) => void;
  setReduxState: (reduxState: TableEditableReduxState) => void;
  setSpreadsheetState: (spreadsheetState: TableEditableSpreadsheetState) => void;
  setMutations: (mutations: TableMutationMethods) => void;
  setState: (state: Partial<TableEditableState>) => void;
}

/** Default editor context when none is provided (e.g., in tests or standalone tables). */
const DEFAULT_EDITOR_CONTEXT: EditorPropertiesContextInterface = {
  showFormulas: false,
  isConfig: false,
};

/**
 * This creates a new store for a structured table and holds all of the props and state for a TableEditable
 * component.  This is intended to be the sole source of truth for the inputs and state of TableEditable,
 * and all selectors and logic should derive models from this store.
 *
 * TableEditable's props are split into stable and volatile props.
 * All volatile props use a deep-compare selector to ensure that the component only re-renders when absolutely
 * necessary.  So any props that are very frequently changing should be placed here.
 *
 * All other props should go in stableProps, which are shallow-compare memoized.  All props in here should
 * not change object references unless they are actually changing.
 */
const tableStoreCreatorForType = create<TableEditableStore>();
type TableStoreHookType = ReturnType<typeof tableStoreCreatorForType>;
export function createTableEditableStore({
  stableProps,
  volatileProps,
  editorContext = DEFAULT_EDITOR_CONTEXT,
  reduxState = DEFAULT_TABLE_EDITABLE_REDUX_STATE,
  spreadsheetState = DEFAULT_TABLE_EDITABLE_SPREADSHEET_STATE,
  mutations = UNAVAILABLE_TABLE_MUTATIONS,
}: {
  stableProps: TableEditableStableProps;
  volatileProps: TableEditableVolatileProps;
  editorContext?: EditorPropertiesContextInterface;
  reduxState?: TableEditableReduxState;
  spreadsheetState?: TableEditableSpreadsheetState;
  mutations?: TableMutationMethods;
}): TableStoreHookType {
  return create<TableEditableStore>()(
    (set) =>
      ({
        stableProps,
        volatileProps,
        editorContext,
        reduxState,
        spreadsheetState,
        mutations,
        state: TABLE_EDITABLE_DEFAULT_STATE,
        setProps: (stableProps, volatileProps) => {
          set((store: TableEditableStore) => ({
            ...store,
            stableProps,
            volatileProps,
          }));
        },
        setEditorContext: (editorContext) => {
          set((store: TableEditableStore) => ({
            ...store,
            editorContext,
          }));
        },
        setReduxState: (reduxState) => {
          set((store: TableEditableStore) => ({
            ...store,
            reduxState,
          }));
        },
        setSpreadsheetState: (spreadsheetState) => {
          set((store: TableEditableStore) => ({
            ...store,
            spreadsheetState,
          }));
        },
        setMutations: (mutations) => {
          set((store: TableEditableStore) => ({
            ...store,
            mutations,
          }));
        },
        setState: (updatedState) => {
          set((store: TableEditableStore) => ({
            ...store,
            state: {
              ...store.state,
              ...updatedState,
            },
          }));
        },
      }) satisfies TableEditableStore
  );
}

export const createTableEditableStoreSelector = createSelector.withTypes<TableEditableStore>();
export const createTableEditableStoreStructuredSelector =
  createStructuredSelector.withTypes<TableEditableStore>();
